<template>
  <AppShell
    v-model:current-lang="currentLang"
    variante="produto"
    evitar-banner-mobile
    :mostrar-switcher="!idiomaTravado"
    :auto-idioma="!idiomaTravado"
    @lang-change="onLangChange"
  >
    <!-- ============================================================
         1. HERO — oferta + dois CTAs (Comprar / Suporte)
         ============================================================ -->
    <PageHero
      :logo-src="logoBranco"
      logo-alt="OligoCheck"
      :badge="t.heroBadge"
      badge-variante="azul"
      :subtitulo="t.heroSubtitulo"
    >
      <template #titulo>
        <span class="titulo-antes">{{ t.heroTituloAntes }}</span>
        <span class="ds-texto-gradiente">{{ t.heroTituloDestaque }}</span>
        <span class="titulo-depois">{{ t.heroTituloDepois }}</span>
      </template>

      <template #ctas>
        <p class="caminhos-pergunta">{{ t.caminhosPergunta }}</p>

        <div class="caminhos-grid">
          <a
            href="#formulario-compra"
            @click.prevent="scrollToForm"
            class="caminho-card caminho-compra"
          >
            <div class="caminho-icone">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="9" cy="21" r="1"/>
                <circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
              </svg>
            </div>
            <div class="caminho-conteudo">
              <span class="caminho-label">{{ t.caminhoCompraLabel }}</span>
              <strong class="caminho-titulo">{{ t.caminhoCompraTitulo }}</strong>
              <span class="caminho-desc">{{ t.caminhoCompraDesc }}</span>
            </div>
            <div class="caminho-seta">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </div>
          </a>

          <a
            :href="whatsappSuporteUrl"
            target="_blank"
            rel="noopener"
            class="caminho-card caminho-suporte"
          >
            <div class="caminho-icone suporte-icone">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <div class="caminho-conteudo">
              <span class="caminho-label">{{ t.caminhoSuporteLabel }}</span>
              <strong class="caminho-titulo">{{ t.caminhoSuporteTitulo }}</strong>
              <span class="caminho-desc">{{ t.caminhoSuporteDesc }}</span>
            </div>
            <div class="caminho-seta">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </div>
          </a>
        </div>
      </template>

      <template #trust>
        <TrustBar
          :items="[
            { texto: t.trust1, icone: 'shield' },
            { texto: t.trust2, icone: 'clock' },
            { texto: t.trust3, icone: 'users' }
          ]"
        />

        <!-- Selo regulatório por país (ANVISA / INVIMA). Só renderiza nas
             versões /oligocheck/brasil e /oligocheck/colombia. -->
        <div v-if="seloInfo" class="hero-selo">
          <SeloRegulatorio
            :agencia="seloInfo.agencia"
            :logo-src="seloInfo.logoSrc"
            :etiqueta="seloInfo.etiqueta"
            :titulo="seloInfo.titulo"
            :descricao="seloInfo.descricao"
          />
        </div>
      </template>
    </PageHero>

    <!-- ============================================================
         2. ¿CÓMO ES POSIBLE? — quebra a 1ª objeção:
            "parece mágica — por qué funciona de verdad"
            Destaque: intracelular vs extracelular (diferencial único).
         ============================================================ -->
    <SecaoPadrao id="como-es-posible" variante="alt">
      <SecaoHeader
        :badge="t.comoBadge"
        :titulo-antes="t.comoTituloAntes"
        :titulo-destaque="t.comoTituloDestaque"
        :titulo-depois="t.comoTituloDepois"
      />

      <div class="como-grid">
        <div class="como-texto">
          <p class="como-p">{{ t.comoP1 }}</p>
          <p class="como-p">{{ t.comoP2 }}</p>

          <!-- Dois pilares do diferencial OligoCheck
               (sem comparação com outros exames — apenas o que ELE entrega) -->
          <div class="nivel-comparativo">
            <div class="nivel-item nivel-intra">
              <span class="nivel-tag">{{ t.nivelIntraTag }}</span>
              <strong>{{ t.nivelIntraTitulo }}</strong>
              <span class="nivel-desc">{{ t.nivelIntraDesc }}</span>
            </div>
            <div class="nivel-item nivel-intra">
              <span class="nivel-tag">{{ t.nivelTempoTag }}</span>
              <strong>{{ t.nivelTempoTitulo }}</strong>
              <span class="nivel-desc">{{ t.nivelTempoDesc }}</span>
            </div>
          </div>
        </div>

        <div class="produto-visual">
          <div class="produto-visual-glow"></div>
          <img :src="heroDevice" alt="OligoCheck" class="produto-visual-img" />
        </div>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         3. ¿QUÉ ES LA ESPECTROFOTOMETRÍA? — quebra a 2ª objeção:
            "¿es ciencia seria?" — método consagrado em clínica.
         ============================================================ -->
    <SecaoPadrao id="espectrofotometria">
      <SecaoHeader
        :badge="t.espBadge"
        :titulo-antes="t.espTituloAntes"
        :titulo-destaque="t.espTituloDestaque"
        :titulo-depois="t.espTituloDepois"
      />

      <div class="esp-intro">
        <p>{{ t.espP1 }}</p>
        <p>{{ t.espP2 }}</p>
      </div>

      <!-- Áreas em que a espectrofotometria já é usada -->
      <div class="aplicacoes">
        <span class="aplicacoes-label">{{ t.aplicacoesLabel }}</span>
        <div class="aplicacoes-grid">
          <span
            v-for="area in t.aplicacoes"
            :key="area"
            class="aplicacao-chip"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            {{ area }}
          </span>
        </div>
      </div>

      <!-- Bloco técnico: 3 passos automáticos -->
      <div class="tech-box">
        <div class="tech-box-grid">
          <div class="tech-box-icone">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 12h4l3-9 4 18 3-9h4"/>
            </svg>
          </div>
          <div class="tech-box-conteudo">
            <div class="tech-box-label">{{ t.techLabel }}</div>
            <h3 class="tech-box-titulo">{{ t.techTitulo }}</h3>
            <p class="tech-box-desc">{{ t.techDesc }}</p>
            <ul class="tech-box-lista">
              <li>
                <div class="tech-step-body">
                  <strong>{{ t.techPasso1Titulo }}</strong>
                  <span>{{ t.techPasso1Desc }}</span>
                </div>
              </li>
              <li>
                <div class="tech-step-body">
                  <strong>{{ t.techPasso2Titulo }}</strong>
                  <span>{{ t.techPasso2Desc }}</span>
                </div>
              </li>
              <li>
                <div class="tech-step-body">
                  <strong>{{ t.techPasso3Titulo }}</strong>
                  <span>{{ t.techPasso3Desc }}</span>
                </div>
              </li>
            </ul>
            <p class="tech-box-nota">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              {{ t.techNota }}
            </p>
          </div>
        </div>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         3B. VEJA EM AÇÃO — demonstração prática do aparelho em uso.
             Fica entre "¿Qué es la espectrofotometría?" e "¿Quién
             puede utilizar?" para que, após entender a base técnica,
             a pessoa veja imediatamente COMO é a experiência real.
         ============================================================ -->
    <SecaoPadrao id="em-acao" variante="alt" container="sm">
      <SecaoHeader
        :badge="t.acaoBadge"
        :titulo-antes="t.acaoTituloAntes"
        :titulo-destaque="t.acaoTituloDestaque"
        :titulo-depois="t.acaoTituloDepois"
        :subtitulo="t.acaoSubtitulo"
      />

      <div class="acao-player">
        <div class="video-wrapper">
          <!-- TODO: trocar VIDEO_ID abaixo pelo videoId real do vídeo
               demonstrativo do aparelho em uso. Mantido temporariamente
               um videoId existente apenas para renderização. -->
          <iframe
            :src="`https://www.youtube.com/embed/${acaoVideoId}?rel=0&modestbranding=1`"
            title="OligoCheck em ação"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
          ></iframe>
        </div>

        <ul class="acao-bullets">
          <li>
            <span class="acao-bullet-check">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </span>
            <span>{{ t.acaoBullet1 }}</span>
          </li>
          <li>
            <span class="acao-bullet-check">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </span>
            <span>{{ t.acaoBullet2 }}</span>
          </li>
          <li>
            <span class="acao-bullet-check">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </span>
            <span>{{ t.acaoBullet3 }}</span>
          </li>
        </ul>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         4. ¿QUIÉN PUEDE UTILIZAR? — quebra a 3ª objeção:
            "¿sirve para mi tipo de consulta?" — identificação.
         ============================================================ -->
    <SecaoPadrao id="publico">
      <SecaoHeader
        :badge="t.publicoBadge"
        :titulo-antes="t.publicoTituloAntes"
        :titulo-destaque="t.publicoTituloDestaque"
        :titulo-depois="t.publicoTituloDepois"
        :subtitulo="t.publicoSubtitulo"
      />

      <div class="publico-grid">
        <div
          v-for="(p, i) in t.publicos"
          :key="i"
          class="publico-card"
        >
          <div class="publico-icone">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <div class="publico-conteudo">
            <strong>{{ p.titulo }}</strong>
            <span>{{ p.desc }}</span>
          </div>
        </div>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         5. ¿QUIÉNES SOMOS? — quebra a 4ª objeção:
            "¿puedo confiar en la empresa?" — autoridade e origem.
         ============================================================ -->
    <SecaoPadrao id="quienes-somos" variante="alt">
      <SecaoHeader
        :badge="t.somosBadge"
        :titulo-antes="t.somosTituloAntes"
        :titulo-destaque="t.somosTituloDestaque"
        :titulo-depois="t.somosTituloDepois"
      />

      <div class="somos-grid">
        <div class="somos-texto">
          <p>{{ t.somosP1 }}</p>

          <ul class="somos-lista">
            <li
              v-for="item in t.somosLista"
              :key="item"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              <span>{{ item }}</span>
            </li>
          </ul>
        </div>

        <div class="somos-numeros">
          <div class="numero-card">
            <span class="numero-valor">+10</span>
            <small class="numero-label">{{ t.numAnos }}</small>
          </div>
          <div class="numero-card">
            <span class="numero-valor">+12.000</span>
            <small class="numero-label">{{ t.numProf }}</small>
          </div>
          <div class="numero-card">
            <span class="numero-valor">+100</span>
            <small class="numero-label">{{ t.numPaises }}</small>
          </div>
          <div class="numero-card">
            <span class="numero-valor">&lt;5s</span>
            <small class="numero-label">{{ t.numTempo }}</small>
          </div>
        </div>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         6. DEPOIMENTOS EM VÍDEO — prova social de quem já usa.
            Grid de cards com thumb do YouTube. Clique abre o vídeo em
            um modal grande (lightbox) sobreposto à página.
         ============================================================ -->
    <SecaoPadrao id="depoimentos">
      <SecaoHeader
        :badge="t.depBadge"
        :titulo-antes="t.depTituloAntes"
        :titulo-destaque="t.depTituloDestaque"
        :titulo-depois="t.depTituloDepois"
        :subtitulo="t.depSubtitulo"
      />

      <div class="dep-nav" :aria-label="t.depNavAria">
        <div
          v-for="(dep, index) in t.depoimentos"
          :key="dep.videoId + index"
          class="dep-thumb"
          :class="{ 'dep-thumb--ativo': index === videoAtivoIndex }"
        >
          <div class="dep-thumb-img">
            <!-- Enquanto não clicado: thumb + overlay de play (mais leve).
                 Ao clicar: iframe com autoplay assume o lugar no mesmo card.
                 Trocar para outro card interrompe o vídeo anterior
                 automaticamente (o iframe deixa de ser renderizado). -->
            <button
              v-if="index !== videoAtivoIndex"
              type="button"
              class="dep-thumb-trigger"
              :aria-label="`${t.labelAbrirVideo}: ${dep.autor} — ${dep.role}`"
              @click="videoAtivoIndex = index"
            >
              <img
                :src="`https://i.ytimg.com/vi/${dep.videoId}/hqdefault.jpg`"
                :alt="dep.autor"
                loading="lazy"
                width="320"
                height="180"
              />
              <span class="dep-thumb-play" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="6 4 20 12 6 20 6 4" />
                </svg>
              </span>
            </button>
            <iframe
              v-else
              :key="`player-${dep.videoId}-${index}`"
              :src="`https://www.youtube.com/embed/${dep.videoId}?rel=0&modestbranding=1&autoplay=1`"
              :title="`${dep.autor} — ${dep.role}`"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen
            ></iframe>
          </div>
          <div class="dep-thumb-info">
            <strong>{{ dep.autor }}</strong>
            <span>{{ dep.role }}</span>
          </div>
        </div>
      </div>
    </SecaoPadrao>


    <!-- ============================================================
         7. FORMULÁRIO DE COMPRA — CTA principal de conversão.
            (vem antes do FAQ — pessoa convertida não precisa ler FAQ)
         ============================================================ -->
    <SecaoPadrao id="formulario-compra" variante="alt" class="secao-formulario">
      <FormularioLead
        :idioma="currentLang"
        :active-campaign="acConfig"
        :redirect-to="{ name: 'oligocheck-gracias' }"
      />
    </SecaoPadrao>

    <!-- ============================================================
         8. FAQ — última seção, resolve objeções residuais pré-compra.
         ============================================================ -->
    <SecaoPadrao id="faq">
      <SecaoHeader :badge="t.faqBadge">
        <template #titulo>{{ t.faqTitulo }}</template>
      </SecaoHeader>

      <Faq :items="t.faqItems" />
    </SecaoPadrao>

    <!-- FOOTER -->
    <template #rodape>
      <RodapeSite
        marca="OligoCheck"
        :texto="t.rodapeTexto"
        :direitos="t.rodapeDireitos"
      />
    </template>
  </AppShell>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import FormularioLead from '@/components/global/formulario/FormularioLead.vue'
import AppShell from '@/components/global/layout/AppShell.vue'
import PageHero from '@/components/global/layout/PageHero.vue'
import RodapeSite from '@/components/global/layout/RodapeSite.vue'
import SecaoPadrao from '@/components/global/secao/SecaoPadrao.vue'
import SecaoHeader from '@/components/global/secao/SecaoHeader.vue'
import TrustBar from '@/components/global/ui/TrustBar.vue'
import Faq from '@/components/global/ui/Faq.vue'
import SeloRegulatorio from '@/components/global/ui/SeloRegulatorio.vue'
import logoBranco from '@/assets/images/logo-branco.svg'
import heroDevice from '@/assets/images/hero-device.png'
import seloAnvisa from '@/assets/images/selo-anvisa.png'
import seloInvima from '@/assets/images/selo-invima.png'
import { useSeo } from '@/composables/useSeo'

/* ============================================================
   Props de variação por país.
     · pais       — 'global' (default, rota /oligocheck) | 'brasil' | 'colombia'.
                    Define o selo regulatório, overrides de copy e SEO regional.
     · idiomaFixo — quando definido ('pt' | 'es' | 'en'), trava o idioma da
                    página (esconde o LangSwitcher e desliga o auto-detect por IP).
   Defaults preservam 100% o comportamento da landing /oligocheck original.
   ============================================================ */
const props = defineProps({
  pais: {
    type: String,
    default: 'global',
    validator: (v) => ['global', 'brasil', 'colombia'].includes(v)
  },
  idiomaFixo: {
    type: String,
    default: null,
    validator: (v) => v === null || ['es', 'pt', 'en'].includes(v)
  }
})

const idiomaTravado = computed(() => !!props.idiomaFixo)
const currentLang = ref(props.idiomaFixo || 'es')
const router = useRouter()

const onLangChange = () => {
  /* AppShell controla a escolha manual de idioma internamente. */
}

/* ============================================================
   ActiveCampaign (lead de compra) — form_41, dedicado à landing
   /oligocheck. Campos customizados: field[1] = profisso, field[2] = pais
   (mesmo contrato que o FormularioLead já espera por padrão).
   ============================================================ */
const acConfig = {
  endpoint: 'https://eduardgasset95711.activehosted.com/proc.php',
  u: '41',
  f: '41',
  s: '',
  c: '0',
  m: '0',
  act: 'sub',
  v: '2',
  or: '09f6a799-a44c-4b7a-9d8a-d34b73811cb4'
}

/* ============================================================
   WhatsApp de suporte pós-venda. Copy sem menção a "OligoScan".
   ============================================================ */
const whatsappSuporte = '34609644237'
const whatsappSuporteUrl = computed(() => {
  const msgs = {
    es: 'Hola, tengo un equipo OligoCheck y necesito soporte post-venta.',
    pt: 'Olá, tenho um equipamento OligoCheck e preciso de suporte pós-venda.',
    en: 'Hello, I have an OligoCheck device and need post-sale support.'
  }
  const msg = encodeURIComponent(msgs[currentLang.value])
  return `https://api.whatsapp.com/send/?phone=${whatsappSuporte}&text=${msg}`
})

const scrollToForm = () => {
  const el = document.getElementById('formulario-compra')
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const irPara = (path) => {
  router.push(path)
}

/* ============================================================
   Depoimentos em vídeo — play inline
   Cada card começa mostrando a thumb do YouTube. Ao clicar,
   o iframe assume o lugar no mesmo card e toca com autoplay.
   Trocar para outro card faz o iframe anterior ser desmontado
   (v-if), o que interrompe a reprodução automaticamente.
   `null` = nenhum vídeo rodando (estado inicial).
   ============================================================ */
const videoAtivoIndex = ref(null)

/* Sessão B — vídeo do OligoCheck em ação (demonstração do aparelho). */
const acaoVideoId = 'aFiKdDgoDlY'

/* ============================================================
   i18n — ES (principal) / PT / EN
   Copy seguindo quebra de objeções:
     2. ¿Cómo es posible?        — diferencial técnico (intracelular)
     3. ¿Qué es la espectro?     — credibilidade do método
     4. ¿Quién puede usarlo?     — identificação do público
     5. ¿Quiénes somos?          — autoridade da empresa
   ============================================================ */
const translations = {
  es: {
    heroBadge: 'OligoCheck · Análisis Mineral Intracelular',
    heroTituloAntes: 'La tecnología que está',
    heroTituloDestaque: 'revolucionando la práctica',
    heroTituloDepois: ' de los profesionales de la salud.',
    heroSubtitulo:
      'Evalúa más de 40 minerales, vitaminas y metales pesados en tiempo real, directamente en la palma del paciente. No invasivo, sin dolor, sin molestias.',
    caminhosPergunta: '¿Qué necesitas hoy?',
    caminhoCompraLabel: 'QUIERO ADQUIRIRLO',
    caminhoCompraTitulo: 'Hablar con un consultor',
    caminhoCompraDesc: 'Propuesta personalizada según tu perfil profesional y objetivos.',
    caminhoSuporteLabel: 'YA TENGO EL EQUIPO',
    caminhoSuporteTitulo: 'Necesito ayuda',
    caminhoSuporteDesc: 'Atención directa por WhatsApp para usuarios OligoCheck.',
    trust1: 'Registrado y homologado',
    trust2: '+10 años en el mercado',
    trust3: '+12.000 profesionales',

    /* 2. ¿CÓMO ES POSIBLE? */
    comoBadge: 'Cómo funciona',
    comoTituloAntes: '¿Cómo es posible disponer de un análisis',
    comoTituloDestaque: 'preciso y fiable',
    comoTituloDepois: 'de forma tan rápida, sencilla y práctica?',
    comoP1:
      'OligoCheck cuenta con una tecnología espectrofotométrica ultra avanzada que permite leer rápidamente minerales, oligoelementos y metales pesados a nivel intracelular — directamente en la palma de la mano del paciente, sin necesidad de muestras ni laboratorio externo.',
    comoP2:
      'Es una herramienta profesional pensada para integrarse a tu consulta: entrega información objetiva sobre minerales, oligoelementos y metales pesados en pocos segundos, apoyando tus recomendaciones de suplementación, nutrición y estilo de vida.',
    nivelIntraTag: 'Nivel intracelular',
    nivelIntraTitulo: 'Información más profunda',
    nivelIntraDesc: 'Evalúa minerales, oligoelementos y metales pesados directamente en los tejidos vivos, más allá de lo que circula en el momento.',
    nivelTempoTag: 'En tiempo real',
    nivelTempoTitulo: 'En menos de 5 segundos',
    nivelTempoDesc: 'La lectura sucede directamente en tu consulta, apoyando la mano del paciente sobre el sensor óptico.',

    /* 3. ¿QUÉ ES LA ESPECTROFOTOMETRÍA? */
    espBadge: 'La tecnología detrás',
    espTituloAntes: '¿Qué es la tecnología de',
    espTituloDestaque: 'espectrofotometría',
    espTituloDepois: '?',
    espP1:
      'La espectrofotometría es un método analítico cuantitativo que mide la absorción, emisión o reflexión de la luz por compuestos químicos en un rango específico de longitudes de onda.',
    espP2:
      'Se utiliza ampliamente en campos como la química, farmacia, medio ambiente, agroalimentación, biología, medicina clínica e industria — aportando información cuantitativa precisa para estudios, análisis de referencia y control de calidad.',
    aplicacoesLabel: 'Usada en:',
    aplicacoes: ['Química', 'Farmacia', 'Medio ambiente', 'Agroalimentación', 'Biología', 'Medicina clínica', 'Industria'],

    techLabel: '3 pasos automáticos',
    techTitulo: 'Cómo OligoCheck aplica la espectrofotometría',
    techDesc:
      'El aparato utiliza una variante patentada de la espectrofotometría óptica, adaptada a tejidos vivos. La medición ocurre directamente sobre la palma de la mano, de forma totalmente no invasiva y con precisión de nivel laboratorial.',
    techPasso1Titulo: 'Emisión multiespectro',
    techPasso1Desc:
      'Un módulo óptico emite luz en longitudes de onda específicas (UV cercano e infrarrojo), calibradas para cada elemento analizado.',
    techPasso2Titulo: 'Análisis del patrón de absorción',
    techPasso2Desc:
      'Sensores de alta sensibilidad miden la luz reflejada por las capas superficiales de la piel y calculan la concentración intracelular correspondiente.',
    techPasso3Titulo: 'Cálculo algorítmico',
    techPasso3Desc:
      'Un algoritmo propietario cruza los datos con más de 2 millones de mediciones clínicas y entrega +40 parámetros en menos de 5 segundos.',
    techNota: 'Método registrado, no invasivo, sin radiación ionizante y aprobado para uso profesional.',

    /* 3B. VEJA EM AÇÃO (sessão B) */
    acaoBadge: 'OligoCheck en acción',
    acaoTituloAntes: 'Mira el OligoCheck',
    acaoTituloDestaque: 'funcionando',
    acaoTituloDepois: 'en una consulta real',
    acaoSubtitulo:
      'Del primer contacto con el paciente al resultado en pantalla: todo el proceso en menos de 3 minutos, sin muestras ni espera por laboratorio.',
    acaoBullet1: 'Proceso totalmente no invasivo, directo en la mano del paciente',
    acaoBullet2: 'Resultado completo en la pantalla en pocos minutos',
    acaoBullet3: 'Informe visual listo para compartir con el paciente',

    /* 4. ¿QUIÉN PUEDE UTILIZAR? */
    publicoBadge: 'Para quién es',
    publicoTituloAntes: '¿Quién puede utilizar la',
    publicoTituloDestaque: 'tecnología OligoCheck',
    publicoTituloDepois: '?',
    publicoSubtitulo:
      'OligoCheck está dirigido a todos los profesionales sanitarios que necesitan resultados precisos para elegir el tratamiento más adecuado para cada paciente.',
    publicos: [
      { titulo: 'Médicos', desc: 'Medicina general, integrativa, funcional, ortomolecular y deportiva.' },
      { titulo: 'Nutricionistas y nutrólogos', desc: 'Información mineral de apoyo para recomendar suplementación.' },
      { titulo: 'Terapeutas ortomoleculares', desc: 'Apoyo objetivo para protocolos personalizados.' },
      { titulo: 'Naturópatas y terapeutas', desc: 'Lectura precisa del estado mineral e iónico.' },
      { titulo: 'Clínicas estéticas y de longevidad', desc: 'Minerales ligados a piel, cabello y envejecimiento.' },
      { titulo: 'Centros de bienestar y spa médico', desc: 'Servicio premium no invasivo con entrega inmediata.' }
    ],

    /* 5. ¿QUIÉNES SOMOS? */
    somosBadge: 'Quiénes somos',
    somosTituloAntes: 'La empresa',
    somosTituloDestaque: 'pionera',
    somosTituloDepois: 'en análisis mineral intracelular',
    somosP1:
      'OligoCheck lleva más de 10 años en el mercado mundial, presente en toda Europa, África y América Latina.',
    somosLista: [
      'Pioneros en análisis mineral intracelular no invasivo',
      'Presencia consolidada en más de 100 países',
      'Más de 12.000 profesionales sanitarios activos',
      'Formación y soporte continuo en español, portugués e inglés'
    ],
    numAnos: 'años en el mercado',
    numProf: 'profesionales activos',
    numPaises: 'países',
    numTempo: 'por análisis',

    /* 6. DEPOIMENTOS */
    depBadge: 'Lo que dicen los profesionales',
    depTituloAntes: 'Esto es lo que',
    depTituloDestaque: 'algunos de nuestros',
    depTituloDepois: 'amigos profesionales ya usan OligoCheck',
    depSubtitulo:
      'Profesionales de diferentes países ya integraron OligoCheck a su consulta. Mira algunas experiencias reales en video.',
    labelAbrirVideo: 'Reproducir video',
    depNavAria: 'Lista de testimonios en video',
    depoimentos: [
      { videoId: 'GVyNbS7N_A4', autor: 'Ester Villa', role: 'Profesional OligoCheck' },
      { videoId: 'W5vTafnUcIY', autor: 'Dra. Cecilia Zgajnar', role: 'Profesional OligoCheck' },
      { videoId: 'yypfYAVZAy8', autor: 'Dra. Katteryne Torres Eusse', role: 'Profesional OligoCheck' }
    ],

    /* 8. FAQ — última seção */
    faqBadge: 'Preguntas frecuentes',
    faqTitulo: 'Lo que más nos preguntan antes de comprar',
    faqItems: [
      { question: '¿Cuánto cuesta el OligoCheck?', answer: 'El valor depende del tipo de práctica, volumen de uso y condiciones vigentes en tu país. Rellena el formulario y un consultor te envía por WhatsApp una propuesta personalizada con todas las formas de pago disponibles.' },
      { question: '¿Qué incluye la compra?', answer: 'El equipo OligoCheck, software clínico, formación completa (online + acompañamiento), soporte técnico continuo por WhatsApp y actualizaciones gratuitas.' },
      { question: '¿Cuánto tarda la entrega?', answer: 'La entrega suele ocurrir en pocos días hábiles tras la confirmación del pedido, dependiendo de tu país. El consultor te da una previsión precisa durante la propuesta.' },
      { question: '¿Ofrecéis formación al comprar?', answer: 'Sí. Todos los clientes reciben formación completa para operar el equipo, interpretar los resultados y aplicarlos en la consulta, además de soporte continuo.' },
      { question: '¿Puedo financiar la compra?', answer: 'Sí, ofrecemos diferentes formas de pago y financiación según tu país y perfil profesional. El consultor presenta todas las opciones durante la propuesta.' },
      { question: '¿Qué evalúa exactamente?', answer: 'Más de 40 parámetros: minerales esenciales (magnesio, calcio, zinc, hierro, etc.), metales pesados (plomo, mercurio, aluminio, etc.) y marcadores de estrés oxidativo y balance iónico intracelular.' },
      { question: '¿Es realmente no invasivo?', answer: 'Totalmente. La medición se hace apoyando la mano sobre un sensor óptico. Sin agujas, sin muestras, sin radiación ionizante.' },
      { question: '¿OligoCheck es un dispositivo regulado?', answer: 'Sí. OligoCheck está registrado para uso profesional y cumple los requisitos técnicos de dispositivos de análisis no invasivos. El consultor comparte la documentación técnica durante la propuesta.' }
    ],

    rodapeTexto: 'Análisis mineral intracelular en tiempo real para profesionales de la salud.',
    rodapeDireitos: 'Todos los derechos reservados.'
  },

  /* ---------------------- PORTUGUÊS ---------------------- */
  pt: {
    heroBadge: 'OligoCheck · Análise Mineral Intracelular',
    heroTituloAntes: 'A tecnologia que está',
    heroTituloDestaque: 'revolucionando a prática',
    heroTituloDepois: ' dos profissionais de saúde.',
    heroSubtitulo:
      'Avalie mais de 40 minerais, vitaminas e metais pesados em tempo real, direto na palma do paciente. Não invasivo, sem dor, sem desconforto.',
    caminhosPergunta: 'Do que você precisa hoje?',
    caminhoCompraLabel: 'QUERO ADQUIRIR',
    caminhoCompraTitulo: 'Falar com um consultor',
    caminhoCompraDesc: 'Proposta personalizada conforme seu perfil profissional e objetivos.',
    caminhoSuporteLabel: 'JÁ TENHO O EQUIPAMENTO',
    caminhoSuporteTitulo: 'Preciso de ajuda',
    caminhoSuporteDesc: 'Atendimento direto pelo WhatsApp para usuários OligoCheck.',
    trust1: 'Registrado e homologado',
    trust2: '+10 anos no mercado',
    trust3: '+12.000 profissionais',

    comoBadge: 'Como funciona',
    comoTituloAntes: 'Como é possível ter uma análise',
    comoTituloDestaque: 'precisa e confiável',
    comoTituloDepois: 'de forma tão rápida, simples e prática?',
    comoP1:
      'O OligoCheck conta com uma tecnologia espectrofotométrica ultra avançada que permite ler rapidamente minerais, oligoelementos e metais pesados a nível intracelular — direto na palma da mão do paciente, sem necessidade de amostras ou laboratório externo.',
    comoP2:
      'É uma ferramenta profissional pensada para se integrar à sua consulta: entrega informação objetiva sobre minerais, oligoelementos e metais pesados em poucos segundos, apoiando suas recomendações de suplementação, nutrição e estilo de vida.',
    nivelIntraTag: 'Nível intracelular',
    nivelIntraTitulo: 'Informação mais profunda',
    nivelIntraDesc: 'Avalia minerais, oligoelementos e metais pesados diretamente nos tecidos vivos, além do que circula no momento.',
    nivelTempoTag: 'Em tempo real',
    nivelTempoTitulo: 'Em menos de 5 segundos',
    nivelTempoDesc: 'A leitura acontece diretamente na sua consulta, apoiando a mão do paciente sobre o sensor óptico.',

    espBadge: 'A tecnologia por trás',
    espTituloAntes: 'O que é a tecnologia de',
    espTituloDestaque: 'espectrofotometria',
    espTituloDepois: '?',
    espP1:
      'A espectrofotometria é um método analítico quantitativo que mede a absorção, emissão ou reflexão da luz por compostos químicos em um intervalo específico de comprimentos de onda.',
    espP2:
      'É amplamente utilizada em áreas como química, farmácia, meio ambiente, agroalimentação, biologia, medicina clínica e indústria — fornecendo informação quantitativa precisa para estudos, análises de referência e controle de qualidade.',
    aplicacoesLabel: 'Usada em:',
    aplicacoes: ['Química', 'Farmácia', 'Meio ambiente', 'Agroalimentação', 'Biologia', 'Medicina clínica', 'Indústria'],

    techLabel: '3 etapas automáticas',
    techTitulo: 'Como o OligoCheck aplica a espectrofotometria',
    techDesc:
      'O aparelho usa uma variante patenteada da espectrofotometria óptica, adaptada a tecidos vivos. A medição ocorre diretamente na palma da mão, de forma totalmente não invasiva e com precisão de nível laboratorial.',
    techPasso1Titulo: 'Emissão multiespectro',
    techPasso1Desc:
      'Um módulo óptico emite luz em comprimentos de onda específicos (UV próximo e infravermelho), calibrados para cada elemento analisado.',
    techPasso2Titulo: 'Análise do padrão de absorção',
    techPasso2Desc:
      'Sensores de alta sensibilidade medem a luz refletida pelas camadas superficiais da pele e calculam a concentração intracelular correspondente.',
    techPasso3Titulo: 'Cálculo algorítmico',
    techPasso3Desc:
      'Um algoritmo proprietário cruza os dados com mais de 2 milhões de medições clínicas e entrega +40 parâmetros em menos de 5 segundos.',
    techNota: 'Método registrado, não invasivo, sem radiação ionizante e aprovado para uso profissional.',

    /* 3B. VEJA EM AÇÃO (sessão B) */
    acaoBadge: 'OligoCheck em ação',
    acaoTituloAntes: 'Veja o OligoCheck',
    acaoTituloDestaque: 'funcionando',
    acaoTituloDepois: 'numa consulta real',
    acaoSubtitulo:
      'Do primeiro contato com o paciente ao resultado na tela: todo o processo em menos de 3 minutos, sem amostras e sem esperar laboratório.',
    acaoBullet1: 'Processo totalmente não invasivo, direto na mão do paciente',
    acaoBullet2: 'Resultado completo na tela em poucos minutos',
    acaoBullet3: 'Laudo visual pronto para compartilhar com o paciente',

    publicoBadge: 'Para quem é',
    publicoTituloAntes: 'Quem pode utilizar a',
    publicoTituloDestaque: 'tecnologia OligoCheck',
    publicoTituloDepois: '?',
    publicoSubtitulo:
      'O OligoCheck é direcionado a todos os profissionais de saúde que precisam de resultados precisos para escolher o tratamento mais adequado para cada paciente.',
    publicos: [
      { titulo: 'Médicos', desc: 'Medicina geral, integrativa, funcional, ortomolecular e esportiva.' },
      { titulo: 'Nutricionistas e nutrólogos', desc: 'Informação mineral de apoio para recomendar suplementação.' },
      { titulo: 'Terapeutas ortomoleculares', desc: 'Apoio objetivo para protocolos personalizados.' },
      { titulo: 'Naturopatas e terapeutas', desc: 'Leitura precisa do estado mineral e iônico.' },
      { titulo: 'Clínicas estéticas e de longevidade', desc: 'Minerais ligados a pele, cabelo e envelhecimento.' },
      { titulo: 'Centros de bem-estar e spa médico', desc: 'Serviço premium não invasivo com entrega imediata.' }
    ],

    somosBadge: 'Quem somos',
    somosTituloAntes: 'A empresa',
    somosTituloDestaque: 'pioneira',
    somosTituloDepois: 'em análise mineral intracelular',
    somosP1:
      'O OligoCheck está há mais de 10 anos no mercado mundial, presente em toda a Europa, África e América Latina.',
    somosLista: [
      'Pioneiros em análise mineral intracelular não invasiva',
      'Presença consolidada em mais de 100 países',
      'Mais de 12.000 profissionais de saúde ativos',
      'Formação e suporte contínuo em espanhol, português e inglês'
    ],
    numAnos: 'anos no mercado',
    numProf: 'profissionais ativos',
    numPaises: 'países',
    numTempo: 'por análise',

    depBadge: 'O que dizem os profissionais',
    depTituloAntes: 'O que dizem',
    depTituloDestaque: 'alguns dos nossos',
    depTituloDepois: 'colegas profissionais que já usam o OligoCheck',
    depSubtitulo:
      'Profissionais de diferentes países já integraram o OligoCheck à sua consulta. Veja algumas experiências reais em vídeo.',
    labelAbrirVideo: 'Reproduzir vídeo',
    depNavAria: 'Lista de depoimentos em vídeo',
    depoimentos: [
      { videoId: 'GVyNbS7N_A4', autor: 'Ester Villa', role: 'Profissional OligoCheck' },
      { videoId: 'W5vTafnUcIY', autor: 'Dra. Cecilia Zgajnar', role: 'Profissional OligoCheck' },
      { videoId: 'yypfYAVZAy8', autor: 'Dra. Katteryne Torres Eusse', role: 'Profissional OligoCheck' }
    ],

    faqBadge: 'Perguntas frequentes',
    faqTitulo: 'O que mais nos perguntam antes de comprar',
    faqItems: [
      { question: 'Quanto custa o OligoCheck?', answer: 'O valor depende do tipo de prática, volume de uso e condições vigentes no seu país. Preencha o formulário e um consultor envia pelo WhatsApp uma proposta personalizada com todas as formas de pagamento disponíveis.' },
      { question: 'O que a compra inclui?', answer: 'O aparelho OligoCheck, software clínico, treinamento completo (online + acompanhamento), suporte técnico contínuo por WhatsApp e atualizações gratuitas.' },
      { question: 'Quanto tempo leva a entrega?', answer: 'A entrega normalmente ocorre em poucos dias úteis após a confirmação do pedido, dependendo do país. O consultor informa a previsão exata na proposta.' },
      { question: 'Oferecem treinamento ao comprar?', answer: 'Sim. Todos os clientes recebem treinamento completo para operar o aparelho, interpretar os resultados e aplicá-los na consulta, além de suporte contínuo.' },
      { question: 'Posso parcelar a compra?', answer: 'Sim, oferecemos diferentes formas de pagamento e financiamento conforme o país e o perfil profissional. O consultor apresenta todas as opções durante a proposta.' },
      { question: 'O que exatamente ele avalia?', answer: 'Mais de 40 parâmetros: minerais essenciais (magnésio, cálcio, zinco, ferro, etc.), metais pesados (chumbo, mercúrio, alumínio, etc.) e marcadores de estresse oxidativo e balanço iônico intracelular.' },
      { question: 'É realmente não invasivo?', answer: 'Totalmente. A medição é feita apoiando a mão sobre um sensor óptico. Sem agulhas, sem amostras, sem radiação ionizante.' },
      { question: 'O OligoCheck é um dispositivo regulado?', answer: 'Sim. O OligoCheck é registrado para uso profissional e atende aos requisitos técnicos de dispositivos de análise não invasivos. O consultor compartilha a documentação técnica durante a proposta.' }
    ],

    rodapeTexto: 'Análise mineral intracelular em tempo real para profissionais da saúde.',
    rodapeDireitos: 'Todos os direitos reservados.'
  },

  /* ---------------------- ENGLISH ---------------------- */
  en: {
    heroBadge: 'OligoCheck · Intracellular Mineral Analysis',
    heroTituloAntes: 'The technology',
    heroTituloDestaque: 'revolutionizing the practice',
    heroTituloDepois: ' of healthcare professionals.',
    heroSubtitulo:
      'Assess 40+ minerals, vitamins and heavy metals in real time, right from the patient\'s palm. Non-invasive, no pain, no discomfort.',
    caminhosPergunta: 'What do you need today?',
    caminhoCompraLabel: 'I WANT TO BUY',
    caminhoCompraTitulo: 'Talk to a consultant',
    caminhoCompraDesc: 'Personalized proposal based on your professional profile and goals.',
    caminhoSuporteLabel: 'I ALREADY OWN THE DEVICE',
    caminhoSuporteTitulo: 'I need help',
    caminhoSuporteDesc: 'Direct WhatsApp support for OligoCheck users.',
    trust1: 'Registered and certified',
    trust2: '+10 years in the market',
    trust3: '+12,000 professionals',

    comoBadge: 'How it works',
    comoTituloAntes: 'How can an analysis be this',
    comoTituloDestaque: 'accurate and reliable',
    comoTituloDepois: 'and still so fast, simple and practical?',
    comoP1:
      'OligoCheck features an ultra-advanced spectrophotometric technology that quickly reads minerals, trace elements and heavy metals at the intracellular level — directly from the patient\'s palm, with no need for samples or an external lab.',
    comoP2:
      'It\'s a professional tool designed to fit into your practice: it delivers objective information about minerals, trace elements and heavy metals in seconds, supporting your supplementation, nutrition and lifestyle recommendations.',
    nivelIntraTag: 'Intracellular level',
    nivelIntraTitulo: 'Deeper information',
    nivelIntraDesc: 'Reads minerals, trace elements and heavy metals directly in living tissue — beyond what is circulating at that moment.',
    nivelTempoTag: 'Real time',
    nivelTempoTitulo: 'In under 5 seconds',
    nivelTempoDesc: 'Measurement happens directly in your clinic, by placing the patient\'s hand on the optical sensor.',

    espBadge: 'The technology behind',
    espTituloAntes: 'What is',
    espTituloDestaque: 'spectrophotometry',
    espTituloDepois: '?',
    espP1:
      'Spectrophotometry is a quantitative analytical method that measures the absorption, emission or reflection of light by chemical compounds across a specific range of wavelengths.',
    espP2:
      'It is widely used in fields such as chemistry, pharmacy, environment, agri-food, biology, clinical medicine and industry — providing precise quantitative information for studies, reference analyses and quality control.',
    aplicacoesLabel: 'Used in:',
    aplicacoes: ['Chemistry', 'Pharmacy', 'Environment', 'Agri-food', 'Biology', 'Clinical medicine', 'Industry'],

    techLabel: '3 automatic steps',
    techTitulo: 'How OligoCheck applies spectrophotometry',
    techDesc:
      'The device uses a patented variant of optical spectrophotometry adapted to living tissue. Measurement is performed directly on the palm of the hand, completely non-invasively and with laboratory-grade accuracy.',
    techPasso1Titulo: 'Multi-spectrum emission',
    techPasso1Desc:
      'An optical module emits light at specific wavelengths (near UV to infrared), calibrated for each element analyzed.',
    techPasso2Titulo: 'Absorption pattern analysis',
    techPasso2Desc:
      'High-sensitivity sensors measure the light reflected by the surface layers of the skin and calculate the corresponding intracellular concentration.',
    techPasso3Titulo: 'Algorithmic calculation',
    techPasso3Desc:
      'A proprietary algorithm cross-references the data against 2M+ clinical measurements and delivers 40+ parameters in under 5 seconds.',
    techNota: 'Registered method, non-invasive, no ionizing radiation, approved for professional use.',

    /* 3B. SEE IT IN ACTION (section B) */
    acaoBadge: 'OligoCheck in action',
    acaoTituloAntes: 'See OligoCheck',
    acaoTituloDestaque: 'working',
    acaoTituloDepois: 'in a real practice',
    acaoSubtitulo:
      'From the first patient contact to the on-screen result: the full process in under 3 minutes — no samples, no waiting for the lab.',
    acaoBullet1: 'Fully non-invasive, performed directly on the patient\'s hand',
    acaoBullet2: 'Full result on screen in a few minutes',
    acaoBullet3: 'Visual report ready to share with the patient',

    publicoBadge: 'Who it is for',
    publicoTituloAntes: 'Who can use',
    publicoTituloDestaque: 'OligoCheck technology',
    publicoTituloDepois: '?',
    publicoSubtitulo:
      'OligoCheck is designed for every healthcare professional who needs accurate results to choose the most appropriate treatment for each patient.',
    publicos: [
      { titulo: 'Physicians', desc: 'General, integrative, functional, orthomolecular and sports medicine.' },
      { titulo: 'Nutritionists and dietitians', desc: 'Supporting mineral information before recommending supplementation.' },
      { titulo: 'Orthomolecular therapists', desc: 'Objective support for personalized protocols.' },
      { titulo: 'Naturopaths and therapists', desc: 'Precise reading of mineral and ionic status.' },
      { titulo: 'Aesthetic and longevity clinics', desc: 'Minerals linked to skin, hair and aging.' },
      { titulo: 'Wellness centers and medical spas', desc: 'Premium non-invasive service with instant delivery.' }
    ],
    publicoProva: '+12,000 professional users worldwide.',

    somosBadge: 'Who we are',
    somosTituloAntes: 'The',
    somosTituloDestaque: 'pioneer company',
    somosTituloDepois: 'in intracellular mineral analysis',
    somosP1:
      'OligoCheck has been in the global market for more than 10 years, with presence across Europe, Africa and Latin America.',
    somosLista: [
      'Pioneers in non-invasive intracellular mineral analysis',
      'Consolidated presence in more than 100 countries',
      '12,000+ active healthcare professionals',
      'Training and ongoing support in Spanish, Portuguese and English'
    ],
    numAnos: 'years in the market',
    numProf: 'active professionals',
    numPaises: 'countries',
    numTempo: 'per analysis',

    depBadge: 'What professionals say',
    depTituloAntes: 'Here\'s what',
    depTituloDestaque: 'some of our',
    depTituloDepois: 'fellow professionals already using OligoCheck share',
    depSubtitulo:
      'Professionals from different countries already integrated OligoCheck into their practice. Watch a few real experiences on video.',
    labelAbrirVideo: 'Play video',
    depNavAria: 'List of video testimonials',
    depoimentos: [
      { videoId: 'GVyNbS7N_A4', autor: 'Ester Villa', role: 'OligoCheck professional' },
      { videoId: 'W5vTafnUcIY', autor: 'Dr. Cecilia Zgajnar', role: 'OligoCheck professional' },
      { videoId: 'yypfYAVZAy8', autor: 'Dr. Katteryne Torres Eusse', role: 'OligoCheck professional' }
    ],

    faqBadge: 'Frequently asked questions',
    faqTitulo: 'What people ask before buying',
    faqItems: [
      { question: 'How much does OligoCheck cost?', answer: 'The price depends on your type of practice, usage volume and current conditions in your country. Fill out the form and a consultant will send you a tailored proposal on WhatsApp with all available payment options.' },
      { question: 'What does the purchase include?', answer: 'The OligoCheck device, clinical software, full training (online + hands-on), continuous WhatsApp technical support, and free updates.' },
      { question: 'How long does delivery take?', answer: 'Delivery usually takes a few business days after order confirmation, depending on your country. The consultant gives you a precise estimate during the proposal.' },
      { question: 'Is training included?', answer: 'Yes. Every client receives full training on operating the device, interpreting results and applying them in practice — plus continuous support.' },
      { question: 'Can I finance the purchase?', answer: 'Yes, we offer different payment and financing options according to your country and professional profile. The consultant walks you through them during the proposal.' },
      { question: 'What exactly does it assess?', answer: 'Over 40 parameters: essential minerals (magnesium, calcium, zinc, iron, etc.), heavy metals (lead, mercury, aluminum, etc.) and markers of oxidative stress and intracellular ionic balance.' },
      { question: 'Is it really non-invasive?', answer: 'Completely. Measurement is done by placing the hand on an optical sensor. No needles, no samples, no ionizing radiation.' },
      { question: 'Is OligoCheck a regulated medical device?', answer: 'Yes. OligoCheck is registered for professional use and meets the technical requirements for non-invasive analysis devices. The consultant shares the technical documentation during the proposal.' }
    ],

    rodapeTexto: 'Real-time intracellular mineral analysis for health professionals.',
    rodapeDireitos: 'All rights reserved.'
  }
}

/* ============================================================
   Overrides de copy por país — reforço regulatório.
   Mesclados sobre as traduções base apenas nas versões brasil/colombia.
   ============================================================ */
const overridesPais = {
  brasil: {
    pt: {
      heroBadge: 'OligoCheck Brasil · Análise Mineral Intracelular',
      trust1: 'Registrado na ANVISA',
      techNota: 'Método registrado na ANVISA, não invasivo, sem radiação ionizante e aprovado para uso profissional.'
    }
  },
  colombia: {
    es: {
      heroBadge: 'OligoCheck Colombia · Análisis Mineral Intracelular',
      trust1: 'Registrado ante el INVIMA',
      techNota: 'Método registrado ante el INVIMA, no invasivo, sin radiación ionizante y aprobado para uso profesional.'
    }
  }
}

const t = computed(() => {
  const base = translations[currentLang.value]
  const over = overridesPais[props.pais]?.[currentLang.value]
  return over ? { ...base, ...over } : base
})

/* ============================================================
   Selo regulatório exibido no hero das versões por país.
   ============================================================ */
const selosPais = {
  brasil: {
    agencia: 'anvisa',
    logoSrc: seloAnvisa,
    pt: {
      etiqueta: 'Conformidade regulatória',
      titulo: 'Registrado na ANVISA',
      descricao: 'Equipamento em conformidade com os requisitos da Agência Nacional de Vigilância Sanitária.'
    }
  },
  colombia: {
    agencia: 'invima',
    logoSrc: seloInvima,
    es: {
      etiqueta: 'Conformidad regulatoria',
      titulo: 'Registrado ante el INVIMA',
      descricao: 'Equipo conforme a los requisitos del Instituto Nacional de Vigilancia de Medicamentos y Alimentos (INVIMA).'
    }
  }
}

const seloInfo = computed(() => {
  const cfg = selosPais[props.pais]
  if (!cfg) return null
  const txt = cfg[currentLang.value] || cfg[props.idiomaFixo]
  if (!txt) return null
  return { agencia: cfg.agencia, logoSrc: cfg.logoSrc || '', ...txt }
})

/* ============================================================
   SEO — foco exclusivo em "OligoCheck".
   (A página /oligoscan é a única que cobre a keyword "oligoscan".)
   ============================================================ */
const SITE_URL = 'https://portal.oligocheck.es'
const PAGE_PATH = computed(() => {
  if (props.pais === 'brasil') return '/oligocheck/brasil'
  if (props.pais === 'colombia') return '/oligocheck/colombia'
  return '/oligocheck'
})
const OG_IMAGE =
  'https://socheck.nyc3.cdn.digitaloceanspaces.com/6dce6cd1afe525bd7b8999-logo%20(1).svg'

const AGGREGATE_RATING = {
  value: 4.9,
  count: 127,
  bestRating: 5,
  worstRating: 1
}

const seoContent = {
  es: {
    /* Title: ~58 chars — cabe inteiro no SERP desktop (máx ~60) e mobile (~55).
       Fórmula: [Marca] · [Benefício principal] | [Qualificador de intent] */
    title: 'OligoCheck · Análisis Mineral Intracelular en 5s | Oficial',
    /* Description: ~158 chars — cabe inteira (Google corta em 155-160).
       Inclui: marca (keyword), benefício-chave (5s), proof point (+40),
       escopo (minerales, vitaminas, metales pesados), CTA (propuesta oficial). */
    description:
      'OligoCheck: análisis mineral intracelular no invasivo en 5 segundos. Evalúa +40 minerales, vitaminas y metales pesados. Solicita la propuesta oficial.',
    keywords: [
      'oligocheck',
      'oligocheck oficial',
      'comprar oligocheck',
      'oligocheck precio',
      'oligocheck opiniones',
      'oligocheck españa',
      'oligocheck méxico',
      'oligocheck colombia',
      'oligocheck argentina',
      'oligocheck equipo',
      'oligocheck aparato',
      'oligocheck consulta',
      'oligocheck consultorio',
      'oligocheck soporte',
      'oligocheck para médicos',
      'oligocheck nutricionistas',
      'análisis mineral intracelular',
      'análisis minerales palma mano',
      'test metales pesados no invasivo',
      'test vitaminas no invasivo',
      'medición oligoelementos consulta',
      'espectrofotometría mineral',
      'estrés oxidativo análisis',
      'análisis mineral tiempo real'
    ],
    breadcrumbHome: 'Inicio',
    breadcrumbPage: 'OligoCheck',
    imageAlt: 'OligoCheck — aparato de análisis mineral intracelular profesional, no invasivo, con resultados en 5 segundos.'
  },
  pt: {
    title: 'OligoCheck · Análise Mineral Intracelular em 5s | Oficial',
    description:
      'OligoCheck: análise mineral intracelular não invasiva em 5 segundos. Avalia +40 minerais, vitaminas e metais pesados. Solicite a proposta oficial.',
    keywords: [
      'oligocheck',
      'oligocheck oficial',
      'comprar oligocheck',
      'oligocheck preço',
      'oligocheck valor',
      'oligocheck opiniões',
      'oligocheck brasil',
      'oligocheck portugal',
      'oligocheck aparelho',
      'oligocheck equipamento',
      'oligocheck consulta',
      'oligocheck consultório',
      'oligocheck suporte',
      'oligocheck para médicos',
      'oligocheck nutricionistas',
      'análise mineral intracelular',
      'análise minerais palma da mão',
      'teste metais pesados não invasivo',
      'teste vitaminas não invasivo',
      'medição oligoelementos consulta',
      'espectrofotometria mineral',
      'estresse oxidativo análise',
      'análise mineral tempo real'
    ],
    breadcrumbHome: 'Início',
    breadcrumbPage: 'OligoCheck',
    imageAlt: 'OligoCheck — aparelho de análise mineral intracelular profissional, não invasivo, com resultados em 5 segundos.'
  },
  en: {
    title: 'OligoCheck · Intracellular Mineral Analysis in 5s | Official',
    description:
      'OligoCheck: non-invasive intracellular mineral analysis in 5 seconds. Assesses 40+ minerals, vitamins and heavy metals. Request the official proposal.',
    keywords: [
      'oligocheck',
      'oligocheck official',
      'buy oligocheck',
      'oligocheck price',
      'oligocheck reviews',
      'oligocheck device',
      'oligocheck equipment',
      'oligocheck practice',
      'oligocheck clinic',
      'oligocheck support',
      'oligocheck for doctors',
      'oligocheck for nutritionists',
      'intracellular mineral analysis',
      'palm hand mineral analysis',
      'non-invasive heavy metals test',
      'non-invasive vitamins test',
      'in-office trace elements measurement',
      'mineral spectrophotometry',
      'oxidative stress analysis',
      'real-time mineral analysis'
    ],
    breadcrumbHome: 'Home',
    breadcrumbPage: 'OligoCheck',
    imageAlt: 'OligoCheck — professional intracellular mineral analysis device, non-invasive, with 5-second results.'
  }
}

const baseUrl = `${SITE_URL}${PAGE_PATH.value}`

/* Canonical/hreflang:
   - Versões por país (idioma travado): URL única, sem ?lang.
   - Versão global (/oligocheck): 3 idiomas via parâmetro ?lang. */
const canonicalPorLang = idiomaTravado.value
  ? { [props.idiomaFixo]: baseUrl }
  : {
      es: baseUrl,
      pt: `${baseUrl}?lang=pt`,
      en: `${baseUrl}?lang=en`
    }

/* Overrides de SEO por país — title, description, keywords regionais. */
const seoPaisOverride = {
  brasil: {
    pt: {
      title: 'OligoCheck Brasil · Análise Mineral Intracelular | ANVISA',
      description:
        'OligoCheck no Brasil: análise mineral intracelular não invasiva em 5 segundos, registrado na ANVISA. Avalia +40 minerais, vitaminas e metais pesados.',
      keywords: [
        'oligocheck',
        'oligocheck brasil',
        'oligocheck anvisa',
        'oligocheck oficial brasil',
        'comprar oligocheck brasil',
        'oligocheck preço',
        'oligocheck valor',
        'oligocheck aparelho',
        'oligocheck equipamento',
        'oligocheck consultório',
        'oligocheck para médicos',
        'oligocheck nutricionistas',
        'análise mineral intracelular',
        'análise minerais palma da mão',
        'teste metais pesados não invasivo',
        'teste vitaminas não invasivo',
        'espectrofotometria mineral',
        'análise mineral tempo real'
      ],
      breadcrumbPage: 'OligoCheck Brasil',
      imageAlt: 'OligoCheck no Brasil — aparelho de análise mineral intracelular não invasivo, registrado na ANVISA, com resultados em 5 segundos.'
    }
  },
  colombia: {
    es: {
      title: 'OligoCheck Colombia · Análisis Mineral Intracelular | INVIMA',
      description:
        'OligoCheck en Colombia: análisis mineral intracelular no invasivo en 5 segundos, registrado ante el INVIMA. Evalúa +40 minerales, vitaminas y metales pesados.',
      keywords: [
        'oligocheck',
        'oligocheck colombia',
        'oligocheck invima',
        'oligocheck oficial colombia',
        'comprar oligocheck colombia',
        'oligocheck precio colombia',
        'oligocheck equipo',
        'oligocheck aparato',
        'oligocheck consultorio',
        'oligocheck para médicos',
        'oligocheck nutricionistas',
        'análisis mineral intracelular',
        'análisis minerales palma mano',
        'test metales pesados no invasivo',
        'test vitaminas no invasivo',
        'espectrofotometría mineral',
        'análisis mineral tiempo real'
      ],
      breadcrumbPage: 'OligoCheck Colombia',
      imageAlt: 'OligoCheck en Colombia — aparato de análisis mineral intracelular no invasivo, registrado ante el INVIMA, con resultados en 5 segundos.'
    }
  }
}

/* ISO date da última modificação desta página — usado pelo WebPage e Product
   para sinalizar freshness ao Google. Atualizar manualmente quando houver
   mudanças significativas de copy ou estrutura. */
const PAGE_LAST_MODIFIED = '2026-04-22'

/* Demonstração do aparelho em uso — mesmo videoId usado na sessão B.
   Fonte verdade: variável `acaoVideoId` no bloco de scripts acima.
   Estrutura separada para manter o schema auto-contido. */
const VIDEO_DEMO_ID = 'aFiKdDgoDlY'
const VIDEO_DEMO_THUMB = `https://i.ytimg.com/vi/${VIDEO_DEMO_ID}/maxresdefault.jpg`

useSeo(() => {
  const lang = currentLang.value
  const seo = {
    ...(seoContent[lang] || seoContent.es),
    ...(seoPaisOverride[props.pais]?.[lang] || {})
  }
  const translation = translations[lang] || translations.es
  const canonical = canonicalPorLang[lang] || baseUrl
  const productId = `${SITE_URL}#product-oligocheck`
  const organizationId = `${SITE_URL}#organization`

  /* ============================================================
     JSON-LD — 8 schemas entrelaçados via @id (grafo Schema.org):
     Organization, WebSite, WebPage, Product, MedicalDevice,
     HowTo, VideoObject, FAQPage, BreadcrumbList.

     Resultados esperados no SERP:
      · Rich snippet de estrelas (AggregateRating no Product)
      · Sitelink de FAQ abaixo do resultado (FAQPage)
      · Rich snippet de passos "como funciona" (HowTo)
      · Vídeo thumbnail + duration (VideoObject)
      · Breadcrumb formatado (BreadcrumbList)
      · Knowledge panel da marca (Organization + WebSite)
     ============================================================ */
  const structuredData = [
    /* --- 1. Organization (fonte única de verdade para dados da empresa) --- */
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': organizationId,
      name: 'OligoCheck',
      alternateName: ['OligoCheck Oficial', 'OligoCheck España'],
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: OG_IMAGE,
        width: 512,
        height: 512
      },
      image: OG_IMAGE,
      foundingDate: '2014',
      description: seo.description,
      sameAs: [
        'https://www.instagram.com/oligocheck_oficial/',
        'https://www.youtube.com/@oligocheck'
      ],
      contactPoint: [{
        '@type': 'ContactPoint',
        telephone: '+34617488404',
        contactType: 'customer support',
        availableLanguage: ['Spanish', 'Portuguese', 'English'],
        areaServed: ['ES', 'PT', 'BR', 'MX', 'AR', 'CO', 'CL', 'US']
      }]
    },

    /* --- 2. WebSite (com SearchAction para sitelinks search box no Google) --- */
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${SITE_URL}#website`,
      url: SITE_URL,
      name: 'OligoCheck',
      description: seo.description,
      inLanguage: ['es', 'pt', 'en'],
      publisher: { '@id': organizationId }
    },

    /* --- 3. WebPage (com speakable para Google Assistant e dateModified) --- */
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${canonical}#webpage`,
      url: canonical,
      name: seo.title,
      description: seo.description,
      inLanguage: lang,
      isPartOf: { '@id': `${SITE_URL}#website` },
      about: { '@id': productId },
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: OG_IMAGE,
        width: 1200,
        height: 630
      },
      datePublished: '2024-01-01',
      dateModified: PAGE_LAST_MODIFIED,
      isAccessibleForFree: true,
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', '[data-speakable]', '.ds-hero-subtitulo']
      },
      breadcrumb: { '@id': `${canonical}#breadcrumb` }
    },

    /* --- 4. Product + MedicalDevice (combinado) — mais rico que Product só --- */
    {
      '@context': 'https://schema.org',
      '@type': ['Product', 'MedicalDevice'],
      '@id': productId,
      name: 'OligoCheck',
      alternateName: 'OligoCheck — Mineral Analysis Device',
      description: seo.description,
      image: [OG_IMAGE],
      brand: { '@type': 'Brand', name: 'OligoCheck' },
      manufacturer: { '@id': organizationId },
      category: 'Medical Device / Non-invasive Mineral Analysis',
      url: canonical,
      keywords: seo.keywords.join(', '),
      purpose: 'Non-invasive intracellular measurement of minerals, vitamins, trace elements and heavy metals.',
      /* Offer com availability e priceCurrency. Sem preço público (B2B) —
         usa-se `price: '0'` + availability PreOrder e `priceSpecification`
         opcional. Para o Google aceitar Product rich snippet sem preço
         público, mantemos `priceValidUntil` em branco e deixamos o CTA. */
      offers: {
        '@type': 'Offer',
        url: canonical,
        priceCurrency: 'EUR',
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        seller: { '@id': organizationId },
        areaServed: ['ES', 'PT', 'BR', 'MX', 'AR', 'CO', 'CL', 'US'],
        availableDeliveryMethod: 'https://schema.org/ParcelService'
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: AGGREGATE_RATING.value,
        reviewCount: AGGREGATE_RATING.count,
        bestRating: AGGREGATE_RATING.bestRating,
        worstRating: AGGREGATE_RATING.worstRating
      }
    },

    /* --- 5. HowTo — como funciona em 3 passos (rich snippet "passos") --- */
    {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      '@id': `${canonical}#howto`,
      name: translation.techTitulo,
      description: translation.techDesc,
      totalTime: 'PT5S',
      supply: [],
      tool: [{ '@type': 'HowToTool', name: 'OligoCheck' }],
      step: [
        {
          '@type': 'HowToStep',
          position: 1,
          name: translation.techPasso1Titulo,
          text: translation.techPasso1Desc
        },
        {
          '@type': 'HowToStep',
          position: 2,
          name: translation.techPasso2Titulo,
          text: translation.techPasso2Desc
        },
        {
          '@type': 'HowToStep',
          position: 3,
          name: translation.techPasso3Titulo,
          text: translation.techPasso3Desc
        }
      ]
    },

    /* --- 6. VideoObject — demo do aparelho (sessão B). Google indexa e
           mostra thumbnail + duration nos resultados de imagem/vídeo. --- */
    {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      '@id': `${canonical}#demo-video`,
      name: `${translation.acaoTituloAntes} ${translation.acaoTituloDestaque} ${translation.acaoTituloDepois}`.trim(),
      description: translation.acaoSubtitulo,
      thumbnailUrl: VIDEO_DEMO_THUMB,
      uploadDate: '2025-09-01',
      contentUrl: `https://www.youtube.com/watch?v=${VIDEO_DEMO_ID}`,
      embedUrl: `https://www.youtube.com/embed/${VIDEO_DEMO_ID}`,
      publisher: { '@id': organizationId },
      inLanguage: lang
    },

    /* --- 7. FAQPage — rich snippet de perguntas (alto CTR) --- */
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${canonical}#faq`,
      mainEntity: (translation.faqItems || []).map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer }
      }))
    },

    /* --- 8. BreadcrumbList --- */
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${canonical}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: seo.breadcrumbHome, item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: seo.breadcrumbPage, item: canonical }
      ]
    }
  ]

  return {
    lang,
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords,
    canonical,
    alternates: idiomaTravado.value
      ? { 'x-default': baseUrl, [props.idiomaFixo]: baseUrl }
      : {
          'x-default': canonicalPorLang.es,
          es: canonicalPorLang.es,
          pt: canonicalPorLang.pt,
          en: canonicalPorLang.en
        },
    image: OG_IMAGE,
    imageAlt: seo.imageAlt,
    type: 'product',
    structuredData
  }
})
</script>

<style scoped>
/* ============================================================
   TÍTULO custom (slot #titulo do PageHero)
   Estrutura: [antes] [destaque em gradiente] [depois]
   "5 segundos" é o destaque visual — é o que mais vende.
   ============================================================ */
.titulo-antes,
.titulo-depois {
  color: var(--ds-text);
  font-family: var(--ds-font-display);
  letter-spacing: -0.5px;
}

/* Selo regulatório (ANVISA/INVIMA) abaixo da TrustBar no hero */
.hero-selo {
  display: flex;
  justify-content: center;
  margin-top: var(--ds-s-5);
}

/* ============================================================
   CAMINHOS (2 CTAs dentro do hero) — mesmo padrão da /oligoscan
   ============================================================ */
.caminhos-pergunta {
  font-family: var(--ds-font-bold);
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: var(--ds-text-dim);
  margin: 0 0 var(--ds-s-5);
}

.caminhos-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--ds-s-4);
  margin-bottom: var(--ds-s-7);
}

.caminho-card {
  display: flex;
  align-items: center;
  gap: var(--ds-s-4);
  text-align: left;
  background: var(--ds-surface-2);
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-r-lg);
  padding: 22px;
  text-decoration: none;
  color: var(--ds-text);
  transition: all var(--ds-t-smooth);
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.caminho-card::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity var(--ds-t-base);
}

.caminho-compra::before {
  background: linear-gradient(135deg, rgba(245, 130, 32, 0.12), transparent 60%);
}
.caminho-suporte::before {
  background: linear-gradient(135deg, rgba(37, 211, 102, 0.12), transparent 60%);
}

.caminho-card:hover::before { opacity: 1; }

.caminho-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--ds-shadow-lg);
}

.caminho-compra:hover { border-color: rgba(245, 130, 32, 0.45); }
.caminho-suporte:hover { border-color: rgba(37, 211, 102, 0.45); }

.caminho-icone {
  min-width: 56px;
  height: 56px;
  border-radius: var(--ds-r-md);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  flex-shrink: 0;
}

.caminho-compra .caminho-icone {
  background: var(--ds-gradient-warning);
  color: #fff;
  box-shadow: var(--ds-shadow-glow-warning);
}

.suporte-icone {
  background: var(--ds-gradient-success);
  color: #fff;
  box-shadow: var(--ds-shadow-glow-success);
}

.caminho-conteudo {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 2px;
  position: relative;
  z-index: 1;
  min-width: 0;
}

.caminho-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--ds-text-dim);
  font-family: var(--ds-font-bold);
}

.caminho-titulo {
  font-family: var(--ds-font-bold);
  font-size: 18px;
  color: var(--ds-text);
  line-height: 1.2;
}

.caminho-desc {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.4;
  margin-top: 2px;
}

.caminho-seta {
  color: var(--ds-text-faint);
  transition: all var(--ds-t-base);
  position: relative;
  z-index: 1;
  flex-shrink: 0;
}

.caminho-card:hover .caminho-seta {
  color: var(--ds-text);
  transform: translateX(4px);
}

/* ============================================================
   2. ¿CÓMO ES POSIBLE? — grid texto + imagem, comparativo e prova
   ============================================================ */
.como-grid {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 60px;
  align-items: center;
}

.como-texto { min-width: 0; }

.como-p {
  font-size: 16px;
  line-height: 1.65;
  color: var(--ds-text-muted);
  margin: 0 0 var(--ds-s-5);
}

.como-p:last-of-type { margin-bottom: var(--ds-s-6); }

/* Dois pilares do OligoCheck (intracelular + tempo real) */
.nivel-comparativo {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: stretch;
  gap: var(--ds-s-3);
  margin-bottom: var(--ds-s-6);
  padding: var(--ds-s-5);
  background: var(--ds-surface-1);
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-r-lg);
}

.nivel-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  border-radius: var(--ds-r-md);
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.nivel-item strong {
  font-family: var(--ds-font-display);
  font-size: 17px;
  line-height: 1.2;
  color: var(--ds-text);
}

.nivel-tag {
  font-family: var(--ds-font-bold);
  font-size: 10.5px;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: var(--ds-text-dim);
}

.nivel-desc {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.68);
  line-height: 1.5;
}

.nivel-intra {
  background: linear-gradient(135deg, rgba(20, 203, 240, 0.08), rgba(26, 175, 149, 0.04));
  border-color: rgba(20, 203, 240, 0.28);
}

.nivel-intra strong {
  background: linear-gradient(135deg, var(--ds-ciano), var(--ds-accent));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

/* Imagem do aparelho (direita) */
.produto-visual {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 380px;
}

.produto-visual-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 90%;
  background: radial-gradient(circle, rgba(20, 203, 240, 0.2) 0%, transparent 70%);
  z-index: 0;
  animation: pulseGlow 5s ease-in-out infinite;
}

@keyframes pulseGlow {
  0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(0.95); }
  50%      { opacity: 0.85; transform: translate(-50%, -50%) scale(1.05); }
}

.produto-visual-img {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 440px;
  height: auto;
  filter: drop-shadow(0 22px 50px rgba(0, 0, 0, 0.4));
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-12px); }
}

/* ============================================================
   3. ¿QUÉ ES LA ESPECTROFOTOMETRÍA? — intro + chips + tech box
   ============================================================ */
.esp-intro {
  max-width: 820px;
  margin: 0 auto var(--ds-s-7);
  text-align: center;
}

.esp-intro p {
  font-size: 16px;
  line-height: 1.7;
  color: var(--ds-text-muted);
  margin: 0 0 var(--ds-s-4);
}

.esp-intro p:last-child { margin-bottom: 0; }

.aplicacoes {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  margin: 0 auto var(--ds-s-8);
  max-width: 820px;
}

.aplicacoes-label {
  font-family: var(--ds-font-bold);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: var(--ds-text-dim);
}

.aplicacoes-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
}

.aplicacao-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  background: var(--ds-surface-2);
  border: 1px solid var(--ds-border-subtle);
  font-size: 13.5px;
  color: var(--ds-text);
  font-family: var(--ds-font-bold);
  transition: all var(--ds-t-base);
}

.aplicacao-chip:hover {
  border-color: var(--ds-accent-border);
  background: var(--ds-accent-soft);
}

.aplicacao-chip svg { color: var(--ds-accent); flex-shrink: 0; }

/* Tech box — consistente com a /oligoscan */
.tech-box {
  background:
    linear-gradient(135deg, rgba(20, 203, 240, 0.06) 0%, rgba(26, 175, 149, 0.04) 100%),
    rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(20, 203, 240, 0.2);
  border-radius: var(--ds-r-xl);
  padding: 36px;
  position: relative;
  overflow: hidden;
}

.tech-box::before {
  content: '';
  position: absolute;
  top: -60px;
  right: -60px;
  width: 220px;
  height: 220px;
  background: radial-gradient(circle, rgba(20, 203, 240, 0.15) 0%, transparent 70%);
  pointer-events: none;
}

.tech-box-grid {
  display: grid;
  grid-template-columns: 60px 1fr;
  gap: 24px;
  position: relative;
  z-index: 1;
}

.tech-box-icone {
  width: 56px;
  height: 56px;
  border-radius: var(--ds-r-md);
  background: linear-gradient(135deg, rgba(20, 203, 240, 0.2), rgba(20, 203, 240, 0.08));
  border: 1px solid rgba(20, 203, 240, 0.3);
  color: var(--ds-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: var(--ds-shadow-glow-azul);
}

.tech-box-label {
  font-family: var(--ds-font-bold);
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--ds-ciano);
  margin-bottom: 6px;
}

.tech-box-titulo {
  font-family: var(--ds-font-display);
  font-size: 22px;
  color: var(--ds-text);
  margin: 0 0 12px;
  line-height: 1.3;
  letter-spacing: -0.3px;
}

.tech-box-desc {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.6;
  margin: 0 0 22px;
}

.tech-box-lista {
  list-style: none;
  padding: 0;
  margin: 0 0 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  counter-reset: tech-steps;
}

.tech-box-lista li {
  display: flex;
  gap: 14px;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  align-items: flex-start;
  counter-increment: tech-steps;
  position: relative;
}

.tech-box-lista li::before {
  content: counter(tech-steps);
  width: 28px;
  height: 28px;
  border-radius: var(--ds-r-sm);
  background: var(--ds-accent-soft);
  color: var(--ds-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--ds-font-bold);
  font-size: 13px;
  border: 1px solid var(--ds-accent-border);
  flex-shrink: 0;
  margin-top: 1px;
}

.tech-step-body { flex: 1; min-width: 0; }

.tech-box-lista li strong {
  display: block;
  font-family: var(--ds-font-bold);
  font-size: 14px;
  color: var(--ds-text);
  margin-bottom: 4px;
  line-height: 1.35;
}

.tech-box-lista li span {
  display: block;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.68);
  line-height: 1.55;
}

.tech-box-nota {
  display: inline-flex;
  align-items: center;
  gap: var(--ds-s-2);
  font-size: 12px;
  color: var(--ds-text-dim);
  margin: 0;
  padding-top: 14px;
  border-top: 1px solid var(--ds-border-subtle);
  font-style: italic;
}

.tech-box-nota svg { color: var(--ds-ciano); flex-shrink: 0; }

/* ============================================================
   4. ¿QUIÉN PUEDE UTILIZAR? — grid de públicos
   ============================================================ */
.publico-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: var(--ds-s-4);
}

.publico-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 20px;
  background: var(--ds-surface-2);
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-r-lg);
  transition: all var(--ds-t-base);
}

.publico-card:hover {
  border-color: var(--ds-accent-border);
  transform: translateY(-2px);
}

.publico-icone {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--ds-accent-soft);
  border: 1px solid var(--ds-accent-border);
  color: var(--ds-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.publico-conteudo {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.publico-conteudo strong {
  font-family: var(--ds-font-bold);
  font-size: 15px;
  color: var(--ds-text);
  line-height: 1.3;
}

.publico-conteudo span {
  font-size: 13.5px;
  color: var(--ds-text-muted);
  line-height: 1.5;
}

/* ============================================================
   5. ¿QUIÉNES SOMOS? — texto + números
   ============================================================ */
.somos-grid {
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: 60px;
  align-items: center;
}

.somos-texto p {
  font-size: 16px;
  line-height: 1.7;
  color: var(--ds-text-muted);
  margin: 0 0 var(--ds-s-4);
}

.somos-texto p strong {
  color: var(--ds-text);
  font-family: var(--ds-font-bold);
}

.somos-lista {
  list-style: none;
  padding: 0;
  margin: var(--ds-s-5) 0 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.somos-lista li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 14.5px;
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.5;
}

.somos-lista li svg {
  color: var(--ds-ciano);
  background: rgba(26, 175, 149, 0.15);
  border-radius: 50%;
  padding: 3px;
  margin-top: 1px;
  flex-shrink: 0;
  box-sizing: content-box;
}

.somos-numeros {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--ds-s-3);
}

.numero-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 26px 18px;
  background: var(--ds-surface-2);
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-r-lg);
  transition: all var(--ds-t-smooth);
  min-height: 120px;
}

.numero-card:hover {
  border-color: rgba(20, 203, 240, 0.3);
  transform: translateY(-2px);
  box-shadow: var(--ds-shadow-lg);
}

.numero-valor {
  font-family: var(--ds-font-display);
  font-size: clamp(28px, 4vw, 36px);
  line-height: 1;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, var(--ds-ciano), var(--ds-accent));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.numero-label {
  font-family: var(--ds-font-bold);
  font-size: 11px;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: var(--ds-text-dim);
  line-height: 1.35;
}

/* ============================================================
   6. DEPOIMENTOS EM VÍDEO
   Grid de cards. Cada card alterna entre thumb (imagem + play)
   e iframe (rodando), mantendo o mesmo tamanho — o vídeo toca
   inline no próprio card.
   ============================================================ */
.video-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--ds-r-xl);
  overflow: hidden;
  background: #000;
  border: 1px solid var(--ds-border);
  box-shadow:
    0 30px 80px -20px rgba(0, 0, 0, 0.65),
    0 0 0 1px rgba(20, 203, 240, 0.1);
}

.video-wrapper iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

/* Grid de miniaturas (3 col no desktop) */
.dep-nav {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--ds-s-4);
}

.dep-thumb {
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: var(--ds-surface-2);
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-r-lg);
  padding: 10px;
  text-align: left;
  color: inherit;
  font-family: inherit;
  transition: border-color var(--ds-t-smooth), box-shadow var(--ds-t-smooth), transform var(--ds-t-smooth);
}

.dep-thumb:has(.dep-thumb-trigger:hover) {
  transform: translateY(-3px);
  border-color: rgba(20, 203, 240, 0.35);
  box-shadow: var(--ds-shadow-lg);
}

.dep-thumb--ativo {
  border-color: var(--ds-accent);
  box-shadow: 0 0 0 3px rgba(20, 203, 240, 0.14), var(--ds-shadow-lg);
  background: linear-gradient(180deg, rgba(20, 203, 240, 0.06), transparent 70%), var(--ds-surface-2);
}

.dep-thumb-img {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--ds-r-md);
  overflow: hidden;
  background: #000;
}

.dep-thumb-img iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}

.dep-thumb-trigger {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  display: block;
}

.dep-thumb-trigger:focus-visible {
  outline: 2px solid var(--ds-accent);
  outline-offset: -2px;
}

.dep-thumb-trigger img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform var(--ds-t-smooth);
}

.dep-thumb-trigger:hover img {
  transform: scale(1.04);
}

.dep-thumb-play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.25);
  transition: all var(--ds-t-base);
  pointer-events: none;
}

.dep-thumb-trigger:hover .dep-thumb-play {
  background: var(--ds-accent);
  border-color: var(--ds-accent);
  color: #06121a;
  transform: translate(-50%, -50%) scale(1.08);
}

.dep-thumb-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 2px 4px 6px;
  min-width: 0;
}

.dep-thumb-info strong {
  font-family: var(--ds-font-bold);
  font-size: 14px;
  color: var(--ds-text);
  line-height: 1.25;
}

.dep-thumb-info span {
  font-size: 12px;
  color: var(--ds-text-dim);
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ============================================================
   Sessão B — Vídeo do aparelho em ação
   Player centralizado + lista de bullets enfatizando os passos.
   ============================================================ */
.acao-player {
  max-width: 820px;
  margin: 0 auto;
}

.acao-bullets {
  list-style: none;
  padding: 0;
  margin: var(--ds-s-5) 0 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.acao-bullets li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--ds-surface-2);
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-r-lg);
  font-size: 15px;
  color: var(--ds-text);
  line-height: 1.45;
}

.acao-bullet-check {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--ds-accent-soft);
  color: var(--ds-accent);
  border: 1px solid var(--ds-accent-border);
  flex-shrink: 0;
}


/* ============================================================
   7. FORMULÁRIO — glow laranja (CTA de compra)
   ============================================================ */
.secao-formulario::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 600px;
  height: 300px;
  background: radial-gradient(circle, rgba(245, 130, 32, 0.08) 0%, transparent 70%);
  pointer-events: none;
}

.secao-formulario :deep(.ds-container) {
  position: relative;
  z-index: 1;
}

/* ============================================================
   RESPONSIVO
   ============================================================ */
@media (max-width: 960px) {
  .como-grid,
  .somos-grid {
    grid-template-columns: 1fr;
    gap: var(--ds-s-7);
  }
  .produto-visual { min-height: auto; order: -1; }
  .produto-visual-img { max-width: 320px; }
}

@media (max-width: 900px) {
  .caminhos-grid { grid-template-columns: 1fr; gap: var(--ds-s-3); }
  .caminho-card { padding: 18px; }
}

@media (max-width: 720px) {
  .tech-box { padding: 26px 20px; border-radius: var(--ds-r-lg); }
  .tech-box-grid { grid-template-columns: 1fr; gap: var(--ds-s-4); }
  .tech-box-icone { width: 48px; height: 48px; }
  .tech-box-titulo { font-size: 19px; }
  .tech-box-desc { font-size: 14px; }

  .nivel-comparativo {
    grid-template-columns: 1fr;
    gap: var(--ds-s-3);
  }

  .somos-numeros { grid-template-columns: 1fr 1fr; gap: var(--ds-s-2); }
  .numero-card { padding: 22px 14px; min-height: 110px; }

  .dep-nav { grid-template-columns: repeat(2, 1fr); gap: var(--ds-s-3); }

  .acao-bullets li { font-size: 14px; padding: 10px 14px; }
}

@media (max-width: 520px) {
  .dep-nav { grid-template-columns: 1fr; }
}
</style>
